import {
  TBCTXKeys,
  TAppDetailsWithAdditionalInfo,
  TBCTXKeysWithoutSize,
  IQcSingleGradingPage,
  IQCLanding,
  TWashingAppDetailsWithAdditionalInfo,
  TCutbankViewDescriptors,
  ILineInSingleGradingPage,
  TStockType,
  TEmbViewDescriptors,
  TEmbUpdateApplicationDataViewDescriptor,
  TEmbAppDetails,
  TBundleLineInViewDescriptors,
} from '@monorepo/types';
import { BCTXComputationHelper, BuildUtils } from '@monorepo/utils';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { IPerPieceLineInTransactions } from 'libs/types/src/lib/input-app/view-descriptors/per-piece-line-in-transactions-page.types';
import { IQCMemberAddition } from 'libs/types/src/lib/input-app/view-descriptors/qc-member-addition.types';
import { IRQCTransactions } from 'libs/types/src/lib/input-app/view-descriptors/rqc-transactions-page.types';
import { IWashingLineInTransactions } from 'libs/types/src/lib/input-app/view-descriptors/washing-transactions-page.types';
import {
  BCTX_SYNC_TIME_DIFFERENCE_IN_MILLI_SECONDS,
  EVENTS_SYNC_TIME_DIFFERENCE_IN_MILLI_SECONDS_FOR_APK,
  TIME_OUT_FOR_SERVER_ERROR,
  WAITING_TIME_FOR_NEXT_EVENTS_SYNCING_IN_MILLI_SECONDS,
  MAX_NO_OF_CARTONS_FOR_AUTO_FILL_AND_COMPLETE,
} from '../metadata/metadata.reducer';
import { selectShifts, selectedShift } from '../shifts/shifts.query';
import * as fromApps from './app-master-db.reducer';
import { ILineInTransactions } from 'libs/types/src/lib/input-app/view-descriptors/line-in-transactions-page.types';
const DEFAULT_TRNASACTION_WIP_TYPE = 'finishing_line';
const selectAppMasterDB = createFeatureSelector<fromApps.TAppMasterDatabase>('appMasterDB');

export const getAppName = createSelector(selectAppMasterDB, (appMaster) => {
  const appNormalizedId = appMaster.result ? appMaster.entities['APP_MASTER'][appMaster.result].apps[0] : false;
  const getAppName = appNormalizedId ? appMaster['entities']['APPS'][appNormalizedId].appInfo.app_name : false;
  return getAppName;
});

export const getAppIdentifier = createSelector(selectAppMasterDB, (appMaster) => {
  if (appMaster.result && appMaster.settingsInfo) {
    return appMaster.entities['APPS'][appMaster.settingsInfo.appId].appInfo.app_identifier;
  } else {
    return false;
  }
});

export const getAllDashboards = createSelector(selectAppMasterDB, (appMaster) => {
  return appMaster.originalAppMasterDb?.apps[0].appInfo.modules[0].view_descriptors[0].dashboards;
});

export const selectedSubject = createSelector(
  selectAppMasterDB,
  (appMaster) => appMaster.settingsInfo && appMaster.entities.SUBJECTS[appMaster.settingsInfo.stationId],
);

export const selectedHasSilhouette = createSelector(
  selectAppMasterDB,
  (appMaster) => appMaster.settingsInfo && appMaster.settingsInfo.hasSilhouette,
);

export const selectedApp = createSelector(
  selectAppMasterDB,
  (appMaster) => appMaster.settingsInfo && appMaster.entities['APPS'][appMaster.settingsInfo.appId],
);

export const selectedAppSettings = createSelector(selectAppMasterDB, (appMaster) => appMaster.settingsInfo);

export const selectedAllViewDescriptor = createSelector(
  selectAppMasterDB,
  (appMaster) => appMaster.settingsInfo && Object.values(appMaster.entities['VIEW_DESCRIPTORS']),
);

export const cutbankInAppInfo = createSelector(
  selectAppMasterDB,
  (appMaster) =>
    appMaster.originalAppMasterDb &&
    appMaster.originalAppMasterDb.apps.filter((app) => app.appInfo.app_identifier === 'cutbank_in_v2')[0],
);

export const embellishmentInAppInfo = createSelector(
  selectAppMasterDB,
  selectedApp,
  (appMaster, selectedApp) =>
    appMaster.originalAppMasterDb &&
    appMaster.originalAppMasterDb.apps.filter(
      (app) =>
        app.appInfo.app_identifier === 'embellishment_in_v2' && app.appInfo.app_id === selectedApp?.appInfo.app_id,
    )[0],
);

export const embellishmentOutAppInfo = createSelector(
  selectAppMasterDB,
  selectedApp,
  (appMaster, selectedApp) =>
    appMaster.originalAppMasterDb &&
    appMaster.originalAppMasterDb.apps.filter(
      (app) =>
        app.appInfo.app_identifier === 'embellishment_out_v2' && app.appInfo.app_id === selectedApp?.appInfo.app_id,
    )[0],
);

export const embellishmentInModule = createSelector(
  embellishmentInAppInfo,
  (embellishmentIn) => embellishmentIn?.appInfo && embellishmentIn.appInfo.modules[0],
);

export const embellishmentOutModule = createSelector(
  embellishmentOutAppInfo,
  (embellishmentOut) => embellishmentOut?.appInfo && embellishmentOut.appInfo.modules[0],
);

export const embellishmentInViewDescriptors = createSelector(
  embellishmentInAppInfo,
  (embellishmentIn) =>
    embellishmentIn?.appInfo &&
    (embellishmentIn.appInfo.modules[0]?.view_descriptors as unknown as TEmbViewDescriptors[]),
);

export const embellishmentOutViewDescriptors = createSelector(
  embellishmentOutAppInfo,
  (embellishmentOut) =>
    embellishmentOut?.appInfo &&
    (embellishmentOut.appInfo.modules[0]?.view_descriptors as unknown as TEmbViewDescriptors[]),
);

export const cutbankOutAppInfo = createSelector(
  selectAppMasterDB,
  (appMaster) =>
    appMaster.originalAppMasterDb &&
    appMaster.originalAppMasterDb.apps.filter((app) => app.appInfo.app_identifier === 'cutbank_out_v2')[0],
);

export const cutbankInViewDescriptors = createSelector(
  cutbankInAppInfo,
  (cutbankIn) =>
    cutbankIn?.appInfo && (cutbankIn.appInfo.modules[0].view_descriptors as unknown as TCutbankViewDescriptors[]),
);

export const cutbankInModule = createSelector(
  cutbankInAppInfo,
  (cutbankIn) => cutbankIn?.appInfo && cutbankIn.appInfo.modules[0],
);

export const cutbankOutViewDescriptors = createSelector(
  cutbankOutAppInfo,
  (cutbankOut) =>
    cutbankOut?.appInfo && (cutbankOut.appInfo.modules[0].view_descriptors as unknown as TCutbankViewDescriptors[]),
);

export const cutbankOutModule = createSelector(
  cutbankOutAppInfo,
  (cutbankOut) => cutbankOut?.appInfo && cutbankOut.appInfo.modules[0],
);

export const bundleLineInAppInfo = createSelector(
  selectAppMasterDB,
  (appMaster) =>
    appMaster.originalAppMasterDb &&
    appMaster.originalAppMasterDb.apps.filter((app) => app.appInfo.app_identifier === 'bundle_line_in_v2')[0],
);

export const bundleLineInViewDescriptors = createSelector(
  bundleLineInAppInfo,
  (bundleLineIn) =>
    bundleLineIn?.appInfo &&
    (bundleLineIn.appInfo.modules[0].view_descriptors as unknown as TBundleLineInViewDescriptors[]),
);

export const bundleLineInModule = createSelector(
  bundleLineInAppInfo,
  (bundleLineIn) => bundleLineIn?.appInfo && bundleLineIn.appInfo.modules[0],
);

export const selectClientInfo = createSelector(selectAppMasterDB, (appMaster) => {
  return Object.values(appMaster.entities.APP_MASTER)[0].clientInfo;
});

export const selectAppInfo = createSelector(
  selectAppMasterDB,
  (appMaster) => appMaster.settingsInfo && appMaster.entities.APPS[appMaster.settingsInfo.appId],
);

// export const allAppsInfo = createSelector(selectAppMasterDB, (appMaster) => appMaster.entities.APPS);

export const allAppsInfo = createSelector(selectAppMasterDB, (appMaster) => {
  if (appMaster.originalAppMasterDb) {
    return appMaster.originalAppMasterDb.apps;
  } else {
    return [];
  }
});

// NOTE: It Should always return value
// factoryId: string;
// appVersion: string;
// shift: string;
// subject: string;
// deviceId: string;

export const selectAppDetails = createSelector(selectAppMasterDB, selectShifts, (appMaster, shiftInfo) => {
  return {
    factoryId: appMaster.result || '',
    appVersion: BuildUtils.APP_VERSION,
    shift: shiftInfo.selectShiftId || '',
    subject: appMaster.settingsInfo ? appMaster.settingsInfo.stationId : '',
    deviceId:
      (appMaster.result && appMaster.entities.APP_MASTER[appMaster.result].deviceInfo.deviceid) ||
      'NO_DEVICE_ID_GENERATED',
    platform:
      (appMaster.result && appMaster.entities.APP_MASTER[appMaster.result].deviceInfo.platform) ||
      'NO_PLATFORM_ID_GENERATED',
  };
});

export const getBctxConfig = createSelector(
  selectAppMasterDB,
  (appMaster) =>
    appMaster.settingsInfo && appMaster.entities.BUSINESS_CONTEXT[appMaster.settingsInfo.businessContextId],
);

export const getIsPlanBasedBctx = createSelector(getBctxConfig, (bctxConfig) => bctxConfig?.bctx_type === 'plan');

export const getAppDetailsWithAdditionalInfo = createSelector(
  selectAppMasterDB,
  selectAppInfo,
  selectedShift,
  selectedSubject,
  getBctxConfig,
  (appMaster, app, shift, subject, bctxConfig) => {
    const appDetailsWithAdditionalInfo: TAppDetailsWithAdditionalInfo = {
      factoryId: appMaster.result || '',
      appVersion: BuildUtils.APP_VERSION,
      shift: shift ? shift.id + '_D_' + BCTXComputationHelper.dashFormateDate() : '',
      subject: appMaster.settingsInfo ? appMaster.settingsInfo.stationId : '',
      deviceId:
        (appMaster.result && appMaster.entities.APP_MASTER[appMaster.result].deviceInfo.deviceid) ||
        'NO_DEVICE_ID_GENERATED',
      appType: app ? app.appInfo.product_type : 'no_app_type',
      an: app ? app.appInfo.app_name : 'no_app_name',
      dnm: subject ? subject.name : '',
      dnr: subject ? subject.gatev : '',
      snm: subject ? subject.dis_name : '',
      dnrk: subject ? subject.gatek : '',
      shiftDn: shift ? shift.name : '',
      urId: '',
      urNm: '',
      wipType: bctxConfig?.bctx_name || 'unknown',
      qtyValidation: bctxConfig?.qty_validation || 'unknown',
    };
    return appDetailsWithAdditionalInfo;
  },
);

export const selectDeviceInfo = createSelector(
  selectAppMasterDB,
  (appMaster) => Object.values(appMaster.entities.APP_MASTER)[0].deviceInfo,
);

export const selectedBusinessContext = createSelector(
  selectAppMasterDB,
  (appMaster) =>
    appMaster.settingsInfo && appMaster.entities.BUSINESS_CONTEXT[appMaster.settingsInfo.businessContextId],
);

export const selectedModule = createSelector(
  selectAppMasterDB,
  (appMaster) =>
    appMaster.settingsInfo && appMaster.entities.MODULES && appMaster.entities.MODULES[appMaster.settingsInfo.moduleId],
);

export const getAllStationIds = createSelector(
  selectAppMasterDB,
  (appMaster) => appMaster.settingsInfo && appMaster.entities.APPS[appMaster.settingsInfo.appId].settingInfo.subjects,
);

export const getAllStations = createSelector(
  selectAppMasterDB,
  (appMaster) =>
    appMaster.settingsInfo &&
    appMaster.entities.APPS[appMaster.settingsInfo.appId].settingInfo.subjects.map(
      (v) => appMaster.entities.SUBJECTS[v],
    ),
);

export const getAllStationIdsV2 = createSelector(selectAppMasterDB, (appMaster) => {
  if (!appMaster.originalAppMasterDb || appMaster.settingsInfo?.appId) {
    return undefined;
  }
  const app = appMaster.originalAppMasterDb.apps.find(
    (app) => app.appInfo.app_identifier === appMaster.settingsInfo?.appId,
  );
  return app ? app.settingInfo.subjects.map((v) => v.subject) : undefined;
});

export const getAllStationsV2 = createSelector(selectAppMasterDB, (appMaster) => {
  if (!appMaster.originalAppMasterDb || !appMaster.settingsInfo?.appId) {
    return undefined;
  }
  const app = appMaster.originalAppMasterDb.apps.find((app) => app.appInfo.app_id === appMaster.settingsInfo?.appId);
  return app ? app.settingInfo.subjects : undefined;
});

export const selectedLandingViewDescriptor = createSelector(selectAppMasterDB, (appMaster) => {
  console.log('test');
  return appMaster.settingsInfo && appMaster.entities.VIEW_DESCRIPTORS[appMaster.settingsInfo.landingViewDescriptorId];
});

export const getSubjectKey = createSelector(selectAppMasterDB, (selectAppMasterDB) => {
  return (
    (selectAppMasterDB.result &&
      selectAppMasterDB.entities['APP_MASTER'][selectAppMasterDB.result].clientInfo.subject_key) ||
    'no_key_subject'
  );
});

export const getSubjectKeyLandingPageAndCurrentModule = createSelector(
  getSubjectKey,
  selectedLandingViewDescriptor,
  selectedModule,
  (subjectKey, landingPage, module) => {
    return {
      subjectKey: subjectKey,
      landingPage: landingPage,
      module: module,
    };
  },
);

export const getBctxViz = createSelector(
  selectAppMasterDB,
  (appMaster) =>
    (appMaster.settingsInfo &&
      appMaster.entities.BUSINESS_CONTEXT[appMaster.settingsInfo.businessContextId].bctx_viz) ||
    [],
);

export const getBuzKeys = createSelector(getBctxViz, (bctxViz) => bctxViz.map((viz) => viz.k));

export const getBctxItemsOrderWithoutSize = createSelector(getBctxViz, (bctxViz) => {
  return bctxViz.filter((viz) => viz.k !== 'size').map((viz) => viz.k) as TBCTXKeysWithoutSize[];
});

export const getBctxKeysToDisplayMap = createSelector(getBctxViz, (bctxViz) => {
  return bctxViz.reduce((acc, bctxInfo) => {
    acc[bctxInfo.k] = bctxInfo.d;
    return acc;
  }, {} as Record<TBCTXKeys, string>);
});

export const getBctxVizAndBctxItemsOrderWithoutSize = createSelector(
  getBctxViz,
  getBctxItemsOrderWithoutSize,
  (bctxViz, order) => {
    return [bctxViz, order] as const;
  },
);

export const getHSKValue = createSelector(selectAppMasterDB, (selectAppMasterDB) => {
  return selectAppMasterDB.settingsInfo
    ? selectAppMasterDB.entities.SUBJECTS[selectAppMasterDB.settingsInfo.stationId].hsk
    : 'primary';
});

export const getUndoValidateInfo = createSelector(
  getBctxConfig,
  selectedModule,
  getAppIdentifier,
  (getBctxConfig, selectedModule, app_identifier) => {
    return {
      validationNeeded: getBctxConfig?.station_type === 'WIP_MID' || app_identifier === 'line_in_v2',
      transactionWipType: selectedModule ? selectedModule.transaction_wip_type : DEFAULT_TRNASACTION_WIP_TYPE,
    };
  },
);

export const getAppSyncItemList = createSelector(
  selectAppMasterDB,
  getAppIdentifier,
  embellishmentInViewDescriptors,
  embellishmentOutViewDescriptors,
  (selectAppMasterDB, app_identifier, embInDescriptors, embOutDescriptors) => {
    if (app_identifier === 'roaming_qc_v2')
      return selectAppMasterDB.entities['VIEW_DESCRIPTORS']['li_app_sync'].component_descriptor;
    else if (app_identifier === 'aql_v2') {
      return selectAppMasterDB.entities['VIEW_DESCRIPTORS']['aql_settings'].component_descriptor;
    } else if (app_identifier === 'line_in_v2') {
      return selectAppMasterDB.entities['VIEW_DESCRIPTORS']['li_app_sync'].component_descriptor;
    } else if (app_identifier === 'embellishment_in_v2' || app_identifier === 'embellishment_out_v2') {
      const descriptors = app_identifier === 'embellishment_in_v2' ? embInDescriptors : embOutDescriptors;
      const syncView = descriptors?.find((view) => view.view_name === 'li_app_sync') as
        | TEmbUpdateApplicationDataViewDescriptor
        | undefined;
      return syncView?.component_descriptor || ([] as TEmbUpdateApplicationDataViewDescriptor['component_descriptor']);
    } else return selectAppMasterDB.entities['VIEW_DESCRIPTORS']['qc_app_sync'].component_descriptor;
  },
);

export const getMemberAdditionViewDescriptor = createSelector(selectAppMasterDB, (selectAppMasterDB) => {
  return selectAppMasterDB.entities['VIEW_DESCRIPTORS']['qc_member_change_view']
    ? (selectAppMasterDB.entities['VIEW_DESCRIPTORS']['qc_member_change_view'] as IQCMemberAddition)
    : undefined;
});

export const getWashingLandingViewDescriptor = createSelector(selectAppMasterDB, (selectAppMasterDB) => {
  return selectAppMasterDB.entities['VIEW_DESCRIPTORS']['washing_landing_page']
    ? (selectAppMasterDB.entities['VIEW_DESCRIPTORS']['washing_landing_page'] as IQCLanding)
    : undefined;
});

export const getQCLandingViewDescriptor = createSelector(selectAppMasterDB, (selectAppMasterDB) => {
  return selectAppMasterDB.entities['VIEW_DESCRIPTORS']['qc_view_builder']
    ? (selectAppMasterDB.entities['VIEW_DESCRIPTORS']['qc_view_builder'] as IQCLanding)
    : undefined;
});

export const getSimpleGradingPageViewDescriptor = createSelector(selectAppMasterDB, (selectAppMasterDB) => {
  return selectAppMasterDB.entities['VIEW_DESCRIPTORS']['qc_simple_grading_page']
    ? (selectAppMasterDB.entities['VIEW_DESCRIPTORS']['qc_simple_grading_page'] as IQcSingleGradingPage)
    : undefined;
});

export const getSimpleLineInGradingPageViewDescriptor = createSelector(selectAppMasterDB, (selectAppMasterDB) => {
  return selectAppMasterDB.entities['VIEW_DESCRIPTORS']['li_simple_grading_page']
    ? (selectAppMasterDB.entities['VIEW_DESCRIPTORS']['li_simple_grading_page'] as ILineInSingleGradingPage)
    : undefined;
});

export const getRqcTransactionsViewDescriptor = createSelector(selectAppMasterDB, (selectAppMasterDB) => {
  return selectAppMasterDB.entities['VIEW_DESCRIPTORS']['rqc_traction_log']
    ? (selectAppMasterDB.entities['VIEW_DESCRIPTORS']['rqc_traction_log'] as IRQCTransactions)
    : undefined;
});

export const getPerPieceLineInTransactionsViewDescriptor = createSelector(selectAppMasterDB, (selectAppMasterDB) => {
  return selectAppMasterDB.entities['VIEW_DESCRIPTORS']['per_piece_line_in_transactions']
    ? (selectAppMasterDB.entities['VIEW_DESCRIPTORS']['per_piece_line_in_transactions'] as IPerPieceLineInTransactions)
    : undefined;
});

export const getLineInTransactionsViewDescriptor = createSelector(selectAppMasterDB, (selectAppMasterDB) => {
  return selectAppMasterDB.entities['VIEW_DESCRIPTORS']['linein_traction_log']
    ? (selectAppMasterDB.entities['VIEW_DESCRIPTORS']['linein_traction_log'] as ILineInTransactions)
    : undefined;
});

export const getWashingTransactionsViewDescriptor = createSelector(selectAppMasterDB, (selectAppMasterDB) => {
  return selectAppMasterDB.entities['VIEW_DESCRIPTORS']['linein_traction_log']
    ? (selectAppMasterDB.entities['VIEW_DESCRIPTORS']['linein_traction_log'] as IWashingLineInTransactions)
    : undefined;
});

export const getTransactionWipTypeAndSubjectKeyAndAppIdentifier = createSelector(
  selectedModule,
  getSubjectKey,
  getAppIdentifier,
  (selectedModule, subjectKey, app_identifier) => {
    return {
      transactionWipType: selectedModule ? selectedModule.transaction_wip_type : DEFAULT_TRNASACTION_WIP_TYPE,
      nonWashingTransactionWipType:
        selectedModule && selectedModule.non_washing_transaction_wip_type
          ? selectedModule.non_washing_transaction_wip_type
          : null,
      subjectKey,
      app_identifier,
    };
  },
);

export const getConfigurations = createSelector(selectedModule, (appInfo) => {
  const eventsSyncTimeDifferenceInMilliSecondsForAPK =
    appInfo && appInfo.events_sync_time_difference_in_milli_seconds_for_apk
      ? appInfo.events_sync_time_difference_in_milli_seconds_for_apk
      : EVENTS_SYNC_TIME_DIFFERENCE_IN_MILLI_SECONDS_FOR_APK;
  return {
    defaultVendorId:
      appInfo && appInfo.bulk_washing_default_vendor_id ? parseInt(appInfo.bulk_washing_default_vendor_id) : undefined,
    isVendorEnabled:
      appInfo && appInfo.bulk_washing_is_vendors_enabled ? appInfo.bulk_washing_is_vendors_enabled : false,
    departmentId:
      appInfo && appInfo.bulk_washing_vendors_department_id ? appInfo.bulk_washing_vendors_department_id : '',
    genericAppType: appInfo && appInfo.bulk_washing_generic_app_type ? appInfo.bulk_washing_generic_app_type : '',
    showOnlyFtt: !!(appInfo && appInfo.show_only_ftt),
    fttBtnText: appInfo && appInfo.ftt_btn_text ? appInfo.ftt_btn_text : '',
    roamingQcMaxSamples: appInfo && appInfo.roaming_qc_max_samples ? appInfo.roaming_qc_max_samples : 10,
    isImageCapturingEnabled: !!(appInfo && appInfo.is_image_capturing_enabled),
    allowPerPieceReworkFromOtherHSK:
      appInfo && appInfo.allow_per_piece_rework_from_other_hsk ? appInfo.allow_per_piece_rework_from_other_hsk : false,
    cacheResetTimestampInMilliSeconds:
      appInfo && appInfo.cache_reset_time_in_milli_seconds ? appInfo.cache_reset_time_in_milli_seconds : 0,
    undoLimit: appInfo && appInfo.undo_limit ? appInfo.undo_limit : 3,
    bctxSyncTimeDifferenceInMilliSeconds:
      appInfo && appInfo.bctx_sync_time_difference_in_milli_seconds
        ? appInfo.bctx_sync_time_difference_in_milli_seconds
        : BCTX_SYNC_TIME_DIFFERENCE_IN_MILLI_SECONDS,
    eventsSyncTimeDifferenceInMilliSecondsForAPK: eventsSyncTimeDifferenceInMilliSecondsForAPK,
    hasCorrectiveActions: appInfo && appInfo.has_corrective_actions ? appInfo.has_corrective_actions : false,
    masterdataLists: appInfo && appInfo.master_data_lists ? appInfo.master_data_lists : [],
    delayedEventsSLAInDays: appInfo && appInfo.delayed_events_sla_in_days ? appInfo.delayed_events_sla_in_days : 3,
    listConf: appInfo && appInfo.listFilter_key ? appInfo['listFilter_key'] : [],
    silhouetteConf: appInfo && appInfo.sil_keys ? appInfo['sil_keys'] : [],
    retryCount: Math.floor(TIME_OUT_FOR_SERVER_ERROR / WAITING_TIME_FOR_NEXT_EVENTS_SYNCING_IN_MILLI_SECONDS),
    syncImmediatelyAfterEventAddition: appInfo && appInfo.sync_immediately_after_event_addition ? true : false,
    locale: appInfo && appInfo.locale ? appInfo.locale : 'default',
    useOperationSilhouetteMapping: appInfo && appInfo.use_operations_silhouette_mapping ? true : false,
    useOperationIssueMappingOnly: appInfo && appInfo.use_operations_issue_mapping_only ? true : false,
    operationsAndIssuesDepartmentId:
      appInfo && appInfo.operations_and_issues_department_id ? appInfo.operations_and_issues_department_id : 'sewing',
    operationsAndIssuesAllDepartmentIds:
      appInfo && appInfo.operations_and_issues_all_department_ids
        ? appInfo.operations_and_issues_all_department_ids
        : undefined,
    maxPerPieceScanTagLimit:
      appInfo && appInfo.max_per_piece_scan_tag_limit ? appInfo.max_per_piece_scan_tag_limit : 50,
    serviceId:
      appInfo && appInfo.ble_mode_service_id ? appInfo.ble_mode_service_id : '6e400001-b5a3-f393-e0a9-e50e24dcca9e',
    characteristicId:
      appInfo && appInfo.ble_mode_characteristic_id
        ? appInfo.ble_mode_characteristic_id
        : '6e400003-b5a3-f393-e0a9-e50e24dcca9e',
    default_roaming_qc_operator_id:
      appInfo && appInfo.default_roaming_qc_operator_id ? appInfo.default_roaming_qc_operator_id : '',
    enablePerPieceTagSearach:
      appInfo && appInfo.enable_per_piece_tag_search ? appInfo.enable_per_piece_tag_search : false,
    enableBundleTagSearch: appInfo && appInfo.enable_bundle_tag_search ? appInfo.enable_bundle_tag_search : false,
    are_roaming_qc_defects_without_category:
      appInfo && appInfo.are_roaming_qc_defects_without_category
        ? appInfo.are_roaming_qc_defects_without_category
        : false,
    hasEnabledForRemoteAppUpdates:
      appInfo && appInfo.has_enabled_for_remote_app_updates ? appInfo.has_enabled_for_remote_app_updates : false,
    min_character_length_for_search_activity_log:
      appInfo && appInfo.min_character_length_for_search_activity_log
        ? appInfo.min_character_length_for_search_activity_log
        : undefined,
    min_character_length_for_search_transactions:
      appInfo && appInfo.min_character_length_for_search_transactions
        ? appInfo.min_character_length_for_search_transactions
        : undefined,
    disable_range_selection: appInfo && appInfo.disable_range_selection ? appInfo.disable_range_selection : false,

    min_character_length_for_search_bctx_selection:
      appInfo && appInfo.min_character_length_for_search_bctx_selection
        ? appInfo.min_character_length_for_search_bctx_selection
        : undefined,
    isRfIdMappingEnabled: appInfo && appInfo.isRfIdMappingEnabled ? appInfo.isRfIdMappingEnabled : false,
    useRFIDScanMode: appInfo && appInfo.use_rfid_scan_mode ? appInfo.use_rfid_scan_mode : false,
    readPowerOfRFID: appInfo && appInfo.read_power_of_rfid ? appInfo.read_power_of_rfid : 500,
    enablePOSplit: appInfo && appInfo.enable_po_split ? true : false,
    viewTechpackEnabled: appInfo && appInfo.view_techpack_enabled ? true : false,
    disableDeviceIdCheck: appInfo && appInfo.disable_device_id_check ? true : false,
    alarmSoundFeatureEnabled: appInfo && appInfo.alarm_sound_feature_enabled ? true : false,
    scanAndPack: appInfo && appInfo.scan_and_pack ? true : false,
    isPrePackEnabled: appInfo && appInfo.is_pre_pack_enabled ? true : false,
    delayWipUpdatesByMin: appInfo && appInfo.delay_wip_updates_by_min ? appInfo.delay_wip_updates_by_min : 0,
    aggregatedLineMapForPerPiece:
      appInfo && appInfo.aggregated_line_map_for_per_piece ? appInfo.aggregated_line_map_for_per_piece : false,
    allowShrinkageUpdate: appInfo && appInfo.allow_shrinkage_update ? appInfo.allow_shrinkage_update : false,
    isWipValidationEnabledLineIn:
      appInfo && appInfo.is_wip_validation_enabled_line_in ? appInfo.is_wip_validation_enabled_line_in : false,
    singleLineMapForLineIn:
      appInfo && appInfo.single_line_map_for_line_in ? appInfo.is_wip_validation_enabled_line_in : false,
    isPendingWipBasedOnActuals:
      appInfo && appInfo.is_pending_wip_based_on_actuals ? appInfo.is_pending_wip_based_on_actuals : false,
    pastDaysEventsStorePerPieceEnabled: appInfo && appInfo.past_days_events_store_per_piece_enabled ? true : false,
    reSyncFailedEventPerPieceEnabled: appInfo && appInfo.resync_failed_events_per_piece_enabled ? true : false,
    sessionHoursValidationEnabled: appInfo && appInfo.session_hours_validation_enabled ? true : false,
    sessionEndBufferTimeInMinutes:
      appInfo && appInfo.session_end_buffer_time_in_minutes ? appInfo.session_end_buffer_time_in_minutes : 10,
    enableTabDataCapture: appInfo && appInfo.enable_tab_data_capture ? true : false,
    stockType: appInfo && appInfo.stock_type ? appInfo.stock_type : TStockType.WITH_BCTX,
    enableCpiDimensionInspectionAndRemark: appInfo && appInfo.enable_cpi_dimension_inspection_and_remark ? true : false,
    enableCpiFttLogicAndPendingWip: appInfo && appInfo.enable_cpi_ftt_logic_and_pending_wip ? true : false,
    enableCpiMultiPanelsSelectionWithSameIssue:
      appInfo && appInfo.enable_cpi_multi_panels_selection_with_same_issue ? true : false,
    enablePreviousDataCapturing: appInfo && appInfo.enable_previous_data_capturing ? true : false,
    enableComponentTagHistorySearch: appInfo && appInfo.enable_component_tag_history_search ? true : false,
    aql_level_department_id: appInfo && appInfo.aql_level_department_id ? appInfo.aql_level_department_id : 'COMMON',
    aql_measurement_department_id:
      appInfo && appInfo.aql_measurement_department_id ? appInfo.aql_measurement_department_id : 'COMMON',
    inspectionWithGrn: appInfo && appInfo.inspection_with_grn ? true : false,
    enablePanelNoCapture: appInfo && appInfo.enable_panel_no_capture ? true : false,
    enableLoadTestingPanel: appInfo && appInfo.enable_load_testing_panel ? true : false,
    useDeviationInputForMeasurementQc: appInfo && appInfo.use_deviation_input_for_measurement_qc ? true : false,
    measurementQcDepartmentId:
      appInfo && appInfo.measurement_qc_department_id ? appInfo.measurement_qc_department_id : 'COMMON',
    enableProductionReasonLogging: appInfo && appInfo.enable_production_reason_logging ? true : false,
    productionReasonDepartmentId:
      appInfo && appInfo.production_reason_department_id ? appInfo.production_reason_department_id : 'common',
    allowZebraBleConnection: appInfo && appInfo.allow_zebra_ble_connection ? true : false,
    enableDifferentBctxScannedMsg: appInfo && appInfo.enable_different_bctx_scanned_msg ? true : false,
    enableRoamingQcRootCauseMapping: appInfo && appInfo.enable_roaming_qc_root_cause_mapping ? true : false,
    isRoamingQcRootCauseMandatory: appInfo && appInfo.is_roaming_qc_root_cause_mandatory ? true : false,
    isRoamingQcCorrectiveActionMandatory: appInfo && appInfo.is_roaming_qc_corrective_action_mandatory ? true : false,
    isBundleLineInNextStation: appInfo && appInfo.is_bundle_line_in_next_station ? true : false,
    enableRoamingQcPreventiveAction: appInfo && appInfo.enable_roaming_qc_preventive_action ? true : false,
    maxNoOfCartonsForAutoFillAndComplete:
      appInfo && appInfo.max_no_of_cartons_for_auto_fill_and_complete
        ? appInfo.max_no_of_cartons_for_auto_fill_and_complete
        : MAX_NO_OF_CARTONS_FOR_AUTO_FILL_AND_COMPLETE,
    isNFCEnabled: !!(appInfo && appInfo.is_nfc_enabled),
  };
});

/**
 * PBI-3272: whether the client DB should hold only the station in use.
 *
 * Receiving In and Receiving Out both run as `line_in_v2`. Without wip validation the app stores its
 * bctx in `planBasedLineMap`, which the line map eviction does not touch - so the config alone is not
 * enough.
 */
export const getIsSingleLineMapForLineInEnabledAndAppIdentifier = createSelector(
  getConfigurations,
  getAppIdentifier,
  ({ singleLineMapForLineIn, isWipValidationEnabledLineIn }, appIdentifier) => {
    return {
      isSingleLineMapForLineInEnabled:
        singleLineMapForLineIn && isWipValidationEnabledLineIn && appIdentifier === 'line_in_v2',
      appIdentifier,
    };
  },
);

export const getIsVendorEnabled = createSelector(getConfigurations, (db) => {
  return db.isVendorEnabled;
});

export const getSubjectKeyWithMasterdataListAndConfs = createSelector(
  getSubjectKey,
  getConfigurations,
  (subjectKey, config) => {
    return {
      subjectKey: subjectKey,
      masterdataList: config.masterdataLists,
      listConf: config.listConf,
      silhouetteConf: config.silhouetteConf,
    };
  },
);

export const getAppDetailsWithAdditionalInfoForWashing = createSelector(
  getAppDetailsWithAdditionalInfo,
  selectedSubject,
  getConfigurations,
  (getAppDetailsWithAdditionalInfo, subject, config) => {
    const appDetailsWithAdditionalInfo: TWashingAppDetailsWithAdditionalInfo = {
      ...getAppDetailsWithAdditionalInfo,
      hsk: subject ? subject.hsk : '',
      genericAppType: config.genericAppType,
    };
    return appDetailsWithAdditionalInfo;
  },
);

export const getAppTypesForEmbellishment = createSelector(
  getAppIdentifier,
  selectAppDetails,
  getAppDetailsWithAdditionalInfo,
  embellishmentInAppInfo,
  embellishmentOutAppInfo,
  (appIdentifier, appDetailsWithShiftIdOnly, appDetails, embInAppInfo, embOutAppInfo) => {
    const baseEmbAppDetails: Partial<TEmbAppDetails> = {
      dnm: appDetails.dnm,
      dnr: appDetails.dnr,
      shiftDn: appDetails.shiftDn,
      snm: appDetails.snm,
      dnrk: appDetails.dnrk,
      an: appDetails.an,
      urId: appDetails.urId,
      urNm: appDetails.urNm,
      ...appDetailsWithShiftIdOnly, // We need shift value as selectShiftId (selectAppDetails) but getAppDetailsWithAdditionalInfo gives shift id + + '_D_' + BCTXComputationHelper.dashFormateDate()
    };
    if (appIdentifier === 'embellishment_in_v2') {
      return {
        ...baseEmbAppDetails,
        currentAppType: embInAppInfo?.settingInfo?.app_type || '',
        embSentAppType: embInAppInfo?.settingInfo?.app_type || '',
        embReceivedAppType: embInAppInfo?.appInfo.validation_product_type || '',
      } as TEmbAppDetails;
    } else {
      return {
        ...baseEmbAppDetails,
        currentAppType: embOutAppInfo?.settingInfo?.app_type || '',
        embReceivedAppType: embOutAppInfo?.settingInfo?.app_type || '',
        embSentAppType: embOutAppInfo?.appInfo.validation_product_type || '',
      } as TEmbAppDetails;
    }
  },
);

export const getTokenAndSelectedSubject = createSelector(
  selectedSubject,
  selectAppMasterDB,
  (selectedSubject, appMaster) => {
    return {
      subject: selectedSubject ? selectedSubject.subject : '',
      hsk: selectedSubject ? selectedSubject.hsk : '',
      token: (appMaster.result && appMaster.entities.APP_MASTER[appMaster.result].activationInfo.token) || '',
      appMasterId: appMaster.originalAppMasterDb?.appMasterId,
      activationToken: appMaster.originalAppMasterDb?.activationInfo.token,
    };
  },
);

export const getBctxItemsOrderWithoutSizeAndTagFields = createSelector(getBctxItemsOrderWithoutSize, (bctxViz) => {
  const ignoreKeys: TBCTXKeysWithoutSize[] = ['search_id', 'bundle_no', 'display_piece_tag', 'audit_disName'];
  return bctxViz.filter((viz) => !ignoreKeys.includes(viz));
});

export const getBctxItemsOrderForBackUp = createSelector(
  getBctxItemsOrderWithoutSize,
  getBctxItemsOrderWithoutSizeAndTagFields,
  getAppIdentifier,
  (orderWithoutSize, orderWithoutSizeAndTagFields, appIdentifier) => {
    return appIdentifier === 'per_piece_qc_v2' ||
      appIdentifier === 'md_point_per_piece_qc_v2' ||
      appIdentifier === 'per_piece_line_in_v2'
      ? orderWithoutSizeAndTagFields
      : orderWithoutSize;
  },
);

export const getSelectedModuleForApp = createSelector(selectAppMasterDB, (appMaster) => {
  const selectedAppId = appMaster.settingsInfo?.appId;
  const selectedApp =
    appMaster.originalAppMasterDb &&
    appMaster.originalAppMasterDb.apps.filter((app) => app.appInfo.app_id === selectedAppId)[0];
  return selectedApp?.appInfo.modules[0];
});

export const getDepartment = createSelector(selectAppInfo, (app) => {
  return app?.appInfo.department || 'COMMON';
});

export const isStationTypeWipEnd = createSelector(
  getBctxConfig,
  (bctxConfig) => bctxConfig?.station_type === 'WIP_END',
);
